import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import './human-in-the-loop.css';
import type { DoneItem } from './types';
import {
  getDoneSync,
  getItemSync,
  getItemsSync,
  groupedSync,
  useHitlVersion,
} from './store';
import { CompactBand, BandLink, BandSep } from '@/components/CompactBand';
// One count source: the band reads the same live store counts the sidebar does
// (rcm-04 Fix-on-staging: band "Posting 9" vs sidebar 13 → one number).
import { useWaitingCount } from '@/features/claims-review/store';
import { usePostingCount } from '@/features/posting/store';
import { useToSendCount } from '@/features/preauths/store';
import { useAppealsWaitingCount } from '@/features/appeals/store';
import { List } from './components/List';
import { Detail } from './components/Detail';
import { CreditDetail } from './components/CreditDetail';
import { DoneHistory } from './components/DoneHistory';
import { DONE_HISTORY, doneInLast30 } from './historyMock';
import { useListUrlState, MOCK_NOW } from '@/components/lists';

const MONTHS3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TODAY_ISO = MOCK_NOW.toISOString().slice(0, 10);
const TODAY_LABEL = `${MONTHS3[MOCK_NOW.getMonth()] ?? 'Oct'} ${String(MOCK_NOW.getDate())}, ${String(MOCK_NOW.getFullYear())}`;

/** Freshly-closed store items get today's date so they sit at the top of history. */
function datedDone(d: DoneItem): DoneItem {
  return d.dateIso ? d : { ...d, dateIso: TODAY_ISO, dateLabel: TODAY_LABEL };
}

function useIsDesktop(): boolean {
  const [d, setD] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true));
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const h = () => { setD(m.matches); };
    m.addEventListener('change', h);
    return () => { m.removeEventListener('change', h); };
  }, []);
  return d;
}

export function Hitl() {
  useHitlVersion();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { id } = useParams();
  const [ls, patchLs] = useListUrlState({ tab: '', q: '', n: '20', win: '30' });
  // One count source for the band — the same live store counts the sidebar reads.
  const claimsCount = useWaitingCount();
  const postingCount = usePostingCount();
  const preauthsCount = useToSendCount();
  const deniedCount = useAppealsWaitingCount();

  const groups = groupedSync();
  const openItems = getItemsSync();
  const selected = id ? getItemSync(id) : null;

  // The Done history reads the live store (freshly-closed items) plus the big
  // mock history. Client-side only; the server owns this in production (§8).
  const doneRows: DoneItem[] = [...getDoneSync().map(datedDone), ...DONE_HISTORY];
  const doneCount30 = doneInLast30(doneRows);
  const doneView = ls.tab === 'done';
  const closeDone = () => { patchLs({ tab: '', q: '', n: '20', win: '30' }); };

  // auto-open first on desktop. Declared before any early return so the hook
  // order stays stable across the queue and Done views.
  useEffect(() => {
    if (!doneView && !id && isDesktop && openItems.length > 0) {
      const first = openItems[0];
      if (first) void navigate(`/human-in-the-loop/${first.id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isDesktop, openItems.length, doneView]);

  if (doneView) {
    return (
      <div className="hl">
        <CompactBand headline={<>What ELVA and the team have closed.</>} />
        <div className="hl-body">
          <DoneHistory
            rows={doneRows}
            q={ls.q}
            win={ls.win === 'all' ? 'all' : '30'}
            loaded={Math.max(20, Number(ls.n) || 20)}
            onSearch={(v) => { patchLs({ q: v, n: '20' }); }}
            onWindow={(v) => { patchLs({ win: v, n: '20' }); }}
            onMore={() => { patchLs({ n: String((Number(ls.n) || 20) + 20) }); }}
            onBack={closeDone}
          />
        </div>
      </div>
    );
  }

  const n = openItems.length;
  const headline =
    n === 0 ? (
      <>Nothing needs a person right now.</>
    ) : n === 1 ? (
      <>
        ELVA tried every way it could on this one. <em>It needs a person.</em>
      </>
    ) : (
      <>
        ELVA tried every way it could on these {n}. <em>They need a person.</em>
      </>
    );

  // "Also waiting for your OK" links — each count is the same number as that
  // page's sidebar count (one count source, rcm-04 Fix-on-staging).
  const waitLinks = [
    { label: 'Claims', to: '/claims', count: claimsCount },
    { label: 'Posting', to: '/posting', count: postingCount },
    { label: 'Pre-auths', to: '/pre-auths', count: preauthsCount },
    { label: 'Appeals', to: '/denied', count: deniedCount },
  ].filter((l) => l.count > 0);
  const right =
    waitLinks.length > 0 ? (
      <>
        <span>Also waiting for your OK:</span>
        {waitLinks.map((l, i) => (
          <Fragment key={l.to}>
            {i > 0 ? <BandSep /> : null}
            <BandLink to={l.to} label={l.label} count={l.count} />
          </Fragment>
        ))}
      </>
    ) : undefined;

  const header = <CompactBand headline={headline} {...(right ? { right } : {})} />;

  if (openItems.length === 0 && !selected && !id) {
    return (
      <div className="hl">
        {header}
        <div className="hl-empty">
          <p className="hl-empty-title">Nobody’s needed right now.</p>
          <p className="hl-meta">ELVA is handling everything. Things land here only when it has tried every way and still needs a person.</p>
          <p className="hl-meta">
            <button type="button" className="hl-src" onClick={() => { patchLs({ tab: 'done' }); }}>
              Done in the last 30 days: {doneCount30.toLocaleString()} · Show
            </button>
          </p>
        </div>
      </div>
    );
  }

  const mobileClass = !isDesktop ? (id ? 'hl-two-detail' : 'hl-two-list') : '';

  let detail: React.ReactNode = null;
  if (id && !selected) {
    detail = <NotFoundInPage as="main" thing="item" id={id} listLabel="items" listTo="/human-in-the-loop" />;
  } else if (selected) {
    // "Can't do this" and "Decide about a claim" both stay inline in card 01
    // (no modal, rcm-04 §6); Detail talks to the store directly.
    detail = selected.kind === 'credit' && selected.credit ? (
      <CreditDetail
        key={selected.id}
        item={selected}
        {...(!isDesktop ? { onBack: () => { void navigate('/human-in-the-loop'); } } : {})}
      />
    ) : (
      <Detail
        key={selected.id}
        item={selected}
        {...(!isDesktop ? { onBack: () => { void navigate('/human-in-the-loop'); } } : {})}
      />
    );
  }

  return (
    <div className={`hl ${mobileClass}`}>
      {header}
      <div className="hl-body">
        <List
          groups={groups}
          doneCount={doneCount30}
          {...(id ? { selectedId: id } : {})}
          onSelect={(iid) => { void navigate(`/human-in-the-loop/${iid}`); }}
          onOpenDone={() => { patchLs({ tab: 'done' }); }}
        />
        {detail}
      </div>
    </div>
  );
}
